import { createContext, useContext, useEffect, useState } from "react";
import { showError, showSuccess } from "../utils/toastUtils";
import { ADMIN_AUTH_KEY } from "../utils/constants";
import api from "../configs/api";

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);

  const [authLoading, setAuthLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);

  /*
  SAVE AUTH
  */
  const saveAuth = (payload) => {
    try {
      localStorage.setItem(ADMIN_AUTH_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error("Save auth failed", error);
    }
  };

  /*
  CLEAR AUTH
  */
  const clearAuth = () => {
    try {
      localStorage.removeItem(ADMIN_AUTH_KEY);
    } catch (error) {
      console.error("Clear auth failed", error);
    }
  };

  /*
  HYDRATE AUTH
  */
  const hydrateAuth = () => {
    try {
      const stored = localStorage.getItem(ADMIN_AUTH_KEY);

      if (!stored) return;

      const parsed = JSON.parse(stored);

      if (!parsed) return;

      setAdmin(parsed.admin);
      setToken(parsed.token);
    } catch (error) {
      console.error("Hydration failed", error);
    }
  };

  /*
  LOGIN ADMIN
  */
  const loginAdmin = async (payload) => {
    try {
      setBtnLoading(true);

      const res = await api.post("/admin/login", payload);

      const { admin, token } = res.data.data;

      setAdmin(admin);
      setToken(token);

      saveAuth({ admin, token });

      showSuccess(`Welcome back, ${admin?.name}`);

      return { success: true };
    } catch (error) {
      console.error(error);

      const msg =
        error?.response?.data?.message || "Login failed. Try again.";

      showError(msg);

      return { success: false };
    } finally {
      setBtnLoading(false);
    }
  };

  /*
  FORGOT PASSWORD
  */
  const forgotPassword = async (payload) => {
    try {
      setBtnLoading(true);

      const res = await api.post("/admin/forgot-password", payload);

      showSuccess(res?.data?.message || "Password reset successful");

      return { success: true };
    } catch (error) {
      console.error(error);

      const msg =
        error?.response?.data?.message ||
        "Password reset failed. Try again.";

      showError(msg);

      return { success: false };
    } finally {
      setBtnLoading(false);
    }
  };

  /*
  FETCH ADMIN PROFILE
  */
  const getAdminById = async (adminId) => {
    try {
      const res = await api.get(`/admin/${adminId}`);

      setAdmin(res.data.data);

      saveAuth({
        admin: res.data.data,
        token,
      });
    } catch (error) {
      console.error(error);
      logoutAdmin();
    }
  };

  /*
  UPDATE ADMIN
  */
  const updateAdmin = async (adminId, payload) => {
    try {
      setBtnLoading(true);

      const res = await api.patch(`/admin/update/${adminId}`, payload);

      setAdmin(res.data.data);

      saveAuth({
        admin: res.data.data,
        token,
      });

      showSuccess("Profile updated successfully");

      return { success: true };
    } catch (error) {
      console.error(error);

      const msg =
        error?.response?.data?.message || "Update failed";

      showError(msg);

      return { success: false };
    } finally {
      setBtnLoading(false);
    }
  };

  /*
  LOGOUT
  */
  const logoutAdmin = () => {
    setAdmin(null);
    setToken(null);

    clearAuth();

    showSuccess("Logged out successfully");
  };

  /*
  INITIAL AUTH CHECK
  */
  useEffect(() => {
    try {
      hydrateAuth();
    } catch (error) {
      console.error(error);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const value = {
    admin,
    token,

    isAuthenticated: !!admin,

    authLoading,
    btnLoading,

    loginAdmin,
    forgotPassword,
    logoutAdmin,

    getAdminById,
    updateAdmin,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

/*
CUSTOM HOOK
*/
export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);

  if (!ctx) {
    throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  }

  return ctx;
};